import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useData } from '@/contexts/DataContext';
import { Sale } from '@/models/sale';

interface TopScreensByMonthProps {
  sales: Sale[];
  selectedYear: string;
}

const TopScreensByMonth: React.FC<TopScreensByMonthProps> = ({
  sales,
  selectedYear
}) => {
  const { screens, ads, expiredAds } = useData();

  const screensByMonth = useMemo(() => {
    // Crear un objeto para rastrear anuncios únicos por pantalla y mes
    const screenAdsByMonth: { [key: number]: { [key: string]: Set<string> } } = {};
    
    // Inicializar el objeto para todos los meses
    for (let i = 0; i < 12; i++) {
      screenAdsByMonth[i] = {};
      screens.forEach(screen => {
        screenAdsByMonth[i][screen.id] = new Set();
      });
    }

    // Función para procesar un anuncio
    const processAd = (ad: any) => {
      const startDate = new Date(ad.startDate);
      const adYear = startDate.getFullYear().toString();
      
      if (adYear === selectedYear) {
        const monthIndex = startDate.getMonth();
        // Para cada pantalla del anuncio
        ad.screens.forEach((screen: { screenId: string }) => {
          if (!screenAdsByMonth[monthIndex][screen.screenId]) {
            screenAdsByMonth[monthIndex][screen.screenId] = new Set();
          }
          screenAdsByMonth[monthIndex][screen.screenId].add(ad.id);
        });
      }
    };

    // Registrar anuncios activos
    ads.forEach(processAd);

    // Registrar anuncios vencidos
    expiredAds.forEach(processAd);

    // Registrar ventas (solo si no está ya contado el anuncio)
    sales.forEach(sale => {
      const saleDate = new Date(sale.date);
      const saleYear = saleDate.getFullYear().toString();
      
      if (saleYear === selectedYear) {
        const monthIndex = saleDate.getMonth();
        if (!screenAdsByMonth[monthIndex][sale.screenId]) {
          screenAdsByMonth[monthIndex][sale.screenId] = new Set();
        }
        screenAdsByMonth[monthIndex][sale.screenId].add(sale.adId);
      }
    });

    // Obtener el mes actual
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear().toString();

    // Convertir a formato final
    return Array.from({ length: 12 }, (_, monthIndex) => {
      const monthName = format(new Date(2024, monthIndex, 1), 'MMMM', { locale: es });
      
      let monthScreens;
      
      // Si es el mes actual y el año actual, mostrar todas las pantallas
      if (monthIndex === currentMonth && selectedYear === currentYear) {
        monthScreens = screens.map(screen => ({
          screenId: screen.id,
          screenName: screen.name,
          count: screenAdsByMonth[monthIndex][screen.id]?.size || 0
        }));
      } else {
        // Para otros meses, solo mostrar pantallas con anuncios
        monthScreens = screens
          .map(screen => ({
            screenId: screen.id,
            screenName: screen.name,
            count: screenAdsByMonth[monthIndex][screen.id]?.size || 0
          }))
          .filter(screen => screen.count > 0);
      }

      // Ordenar por cantidad de anuncios (de mayor a menor)
      monthScreens.sort((a, b) => b.count - a.count);

      return {
        monthIndex,
        monthName,
        screens: monthScreens
      };
    });
  }, [sales, selectedYear, screens, ads, expiredAds]);

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-medium text-foreground">
          Pantallas con colocaciones por mes ({selectedYear})
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {screensByMonth.map((monthData, index) => (
            <div 
              key={`month-${index}`} 
              className="p-4 border border-border rounded-lg bg-muted hover:bg-muted/80 transition-colors"
            >
              <div className="text-sm font-semibold text-foreground capitalize mb-3">
                {monthData.monthName}
              </div>
              {monthData.screens.length > 0 ? (
                <div className="flex flex-col gap-1.5">
                  {monthData.screens.map((screen) => (
                    <div 
                      key={screen.screenId} 
                      className="bg-warning text-warning-foreground rounded px-2 py-1 text-xs flex items-center justify-between font-medium"
                    >
                      <span className="truncate">{screen.screenName.toUpperCase()} - {screen.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  Sin anuncios
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default TopScreensByMonth;
